﻿import React, {createContext, CSSProperties, JSX, ReactNode, useCallback, useContext, useState} from "react";
import Dialog from "./Dialog";

interface DialogState<T = any> {
	content: ((close: (result: T) => void) => ReactNode) | null;
	isOpen: boolean;
	options: DialogOptions;
	resolve: ((value: T) => void) | null;
}

interface DialogContextType {
	showDialog: <T>(
		content: (close: (result: T) => void) => ReactNode,
		options?: DialogOptions
	) => Promise<T>;
}

const DialogContext = createContext<DialogContextType | null>(null);
const defaultDialogOptions: DialogOptions = {title: ""};

export function DialogProvider({children}: { children: ReactNode }): JSX.Element {
	const [dialogState, setDialogState] = useState<DialogState>({
		isOpen: false,
		resolve: null,
		content: null,
		options: defaultDialogOptions
	});

	const showDialog = useCallback(<T, >(
		content: (close: (result: T) => void) => ReactNode,
		options: DialogOptions = defaultDialogOptions
	): Promise<T> =>
		new Promise<T>(resolve =>
			setDialogState(prev => {
				// One dialog slot: opening a second dialog replaces the first, so settle the
				// first promise (as a dismissal) instead of leaving its awaiter hanging forever.
				prev.resolve?.(undefined);
				return {
					isOpen: true,
					resolve,
					content,
					options
				};
			})), []);

	const closeDialog = useCallback((result: any) => {
		if (dialogState.resolve) {
			dialogState.resolve(result);
		}
		setDialogState({
			isOpen: false,
			resolve: null,
			content: null,
			options: defaultDialogOptions
		});
	}, [dialogState]);

	return <DialogContext.Provider value={{showDialog}}>
		{children}
		{dialogState.isOpen && <Dialog title={dialogState.options.title} className={dialogState.options.className} style={dialogState.options.style} onClose={closeDialog}>
			{dialogState.content?.(closeDialog)}
        </Dialog>}
	</DialogContext.Provider>;
}

export interface DialogOptions {
	title: string;
	/** Extra class on the <dialog> itself, for a consumer that needs its own variant. */
	className?: string;
	style?: CSSProperties;
}

export function useDialog(): <T>(content: (close: (result: T) => void) => ReactNode, options?: DialogOptions) => Promise<T> {
	const context = useContext(DialogContext);
	if (!context) {
		throw new Error("useDialog must be used within a DialogProvider");
	}
	return context.showDialog;
}
